"use client";

import { useState, useEffect } from "react";
import Script from "next/script";
import Link from "next/link";
import { Cookie, ChevronDown, X, Save, ShieldCheck } from "lucide-react";
import { siteConfig } from "@/lib/siteConfig";

const ACCEPT_KEY = "cookieConsent";
const REJECT_KEY = "rejectCookieConsent";

const COOKIE_CATEGORIES = [
    {
        id: "essential",
        label: "Essential Cookies",
        required: true,
        description:
            "These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms. You can set your browser to block or alert you about these cookies, but some parts of the site will not then work. These cookies do not store any personally identifiable information.",
        cookies: [
            {
                name: "XSRF-TOKEN",
                host: "velanapps.com",
                duration: "1 Day",
                description:
                    "A security measure used to protect against cross-site request forgery (CSRF) attacks by providing a unique token with each request to verify the request's authenticity.",
            },
            {
                name: "velanapps_session",
                host: "velanapps.com",
                duration: "1 Day",
                description:
                    "Stores session-specific data such as user preferences or login credentials temporarily on your device. Helps enhance the user experience by providing personalized content and maintaining authentication status.",
            },
            {
                name: "cf_clearance",
                host: "velanapps.com",
                duration: "1 Day",
                description:
                    "Associated with Cloudflare. Used as part of Cloudflare's security features to identify and mitigate potential threats such as DDoS attacks and malicious bot traffic.",
            },
        ],
    },
    {
        id: "analytics",
        label: "Analytics Cookies",
        required: false,
        description:
            "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site. All information these cookies collect is aggregated and therefore anonymous. If you do not allow these cookies we will not know when you have visited our site, and will not be able to monitor its performance.",
        cookies: [
            {
                name: "_ga",
                host: "velanapps.com",
                duration: "730 Days",
                description:
                    "Associated with Google Universal Analytics. Used to distinguish unique users by assigning a randomly generated number as a client identifier. Calculates visitor, session and campaign data for analytics reports.",
            },
            {
                name: "_ga_CB4J87ZWD5",
                host: "velanapps.com",
                duration: "729 Days",
                description:
                    "Used by Google Analytics to persist session state and measure site activity. Works in conjunction with the _ga cookie.",
            },
            {
                name: "mf_user",
                host: "velanapps.com",
                duration: "90 Days",
                description:
                    "Identifies unique visitors and tracks their sessions for analytical purposes. Helps analyze user behavior such as mouse movements, clicks, and scrolling to improve website usability.",
            },
            {
                name: "mf_[session-id]",
                host: "velanapps.com",
                duration: "Session",
                description:
                    "Stores a unique identifier for each visitor's session. Allows Mouseflow to aggregate and analyze user interactions on the website to help optimize design and functionality.",
            },
        ],
    },
];

function getRawCookie(name) {
    const match = document.cookie.split(";").find((c) => c.trim().startsWith(`${name}=`));
    return match ? match.split("=").slice(1).join("=").trim() : null;
}

function getCookieConsent() {
    if (getRawCookie(ACCEPT_KEY)) return { essential: true, analytics: true };
    if (getRawCookie(REJECT_KEY)) return { essential: true, analytics: false };
    try {
        if (localStorage.getItem(ACCEPT_KEY)) return { essential: true, analytics: true };
        if (localStorage.getItem(REJECT_KEY)) return { essential: true, analytics: false };
    } catch { /* localStorage blocked */ }
    return null;
}

function logConsent(action, prefs) {
    fetch("/api/cookie-consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            action,
            analytics: prefs.analytics,
            page: window.location.pathname,
        }),
    }).catch(() => {});
}

function saveConsent(prefs) {
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    const past = "Thu, 01 Jan 1970 00:00:00 UTC";
    if (prefs.analytics) {
        document.cookie = `${ACCEPT_KEY}=true; expires=${expires}; path=/; SameSite=Lax`;
        document.cookie = `${REJECT_KEY}=deleted; expires=${past}; path=/`;
        localStorage.setItem(ACCEPT_KEY, "true");
        localStorage.removeItem(REJECT_KEY);
    } else {
        document.cookie = `${REJECT_KEY}=true; expires=${expires}; path=/; SameSite=Lax`;
        document.cookie = `${ACCEPT_KEY}=deleted; expires=${past}; path=/`;
        localStorage.setItem(REJECT_KEY, "true");
        localStorage.removeItem(ACCEPT_KEY);
    }
}


function clearNonEssentialCookies() {
    const hostname = window.location.hostname;
    document.cookie.split(";").forEach((c) => {
        const name = c.trim().split("=")[0];
        if (
            name.startsWith("_ga") ||
            name.startsWith("_gid") ||
            name.startsWith("_gat") ||
            name.startsWith("mf_")
        ) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
        }
    });
}

const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "wheel"];
const IDLE_FALLBACK_MS = 5000;

function useAfterInteractionOrIdle(enabled) {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!enabled || ready) return;
        let timer;
        const fire = () => setReady(true);
        const startTimer = () => { timer = setTimeout(fire, IDLE_FALLBACK_MS); };

        INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, fire, { once: true, passive: true }));
        if (document.readyState === "complete") startTimer();
        else window.addEventListener("load", startTimer, { once: true });

        return () => {
            clearTimeout(timer);
            window.removeEventListener("load", startTimer);
            INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, fire));
        };
    }, [enabled, ready]);

    return [enabled && ready, () => setReady(true)];
}

function CookieTable({ cookies }) {
    return (
        <div className="overflow-x-auto mt-2">
            <table className="w-full border-collapse text-[13px]">
                <thead>
                    <tr className="bg-slate-100">
                        {["Name", "Host", "Duration", "Description"].map((h) => (
                            <th
                                key={h}
                                className="text-left px-3 py-2 border-b border-slate-200 font-semibold text-gray-700 whitespace-nowrap"
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {cookies.map((c, i) => (
                        <tr key={i} className="border-b border-slate-100">
                            <td className="px-3 py-2 font-mono text-red-700 font-medium">{c.name}</td>
                            <td className="px-3 py-2 text-gray-500">{c.host}</td>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{c.duration}</td>
                            <td className="px-3 py-2 text-gray-700 leading-normal">{c.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CategoryAccordion({ category, enabled, onToggle }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="border border-slate-200 rounded-lg mb-3 overflow-hidden">
            <div
                className="flex items-center justify-between px-4 py-3.5 bg-slate-50 cursor-pointer"
                onClick={() => setOpen((v) => !v)}
            >
                <div className="flex items-center gap-3">
                    <ChevronDown
                        size={16}
                        className={`text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    />
                    <div>
                        <div className="font-semibold text-[15px] text-slate-800">{category.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {category.cookies.length} cookie{category.cookies.length !== 1 ? "s" : ""}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {category.required ? (
                        <span className="text-[11px] bg-green-100 text-green-700 border border-green-200 rounded-full px-2.5 py-0.5 font-semibold">
                            Always Active
                        </span>
                    ) : (
                        <button
                            type="button"
                            role="switch"
                            aria-checked={enabled}
                            onClick={() => onToggle(category.id)}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${enabled ? "bg-red-600" : "bg-slate-300"
                                }`}
                        >
                            <span
                                className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-all duration-200 ${enabled ? "left-[23px]" : "left-[3px]"
                                    }`}
                            />
                        </button>
                    )}
                </div>
            </div>

            {open && (
                <div className="p-4 bg-white">
                    <p className="text-[13px] text-slate-600 mb-3 leading-relaxed">{category.description}</p>
                    <CookieTable cookies={category.cookies} />
                </div>
            )}
        </div>
    );
}

export default function CookiePolicy() {
    const [visible, setVisible] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [prefs, setPrefs] = useState({ essential: true, analytics: true });
    const [gtmReady, setGtmReady] = useState(false);
    const [loadGtm, loadGtmNow] = useAfterInteractionOrIdle(gtmReady);

    useEffect(() => {
        if (!gtmReady || window.dataLayer) return;
        window.dataLayer = [{ "gtm.start": new Date().getTime(), event: "gtm.js" }];
    }, [gtmReady]);

    useEffect(() => {
        const consent = getCookieConsent();
        if (consent) {
            // Stored consent is only readable client-side; must run after hydration
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setVisible(false);
            setPrefs(consent);
            if (consent.analytics) setGtmReady(true);
        }
    }, []);

    function handleAcceptAll() {
        const newPrefs = { essential: true, analytics: true };
        setPrefs(newPrefs);
        saveConsent(newPrefs);
        logConsent("ACCEPT_ALL", newPrefs);
        setGtmReady(true);
        loadGtmNow();
        setVisible(false);
        setShowModal(false);
    }

    function handleRejectAll() {
        const newPrefs = { essential: true, analytics: false };
        setPrefs(newPrefs);
        saveConsent(newPrefs);
        logConsent("REJECT_ALL", newPrefs);
        clearNonEssentialCookies();
        setGtmReady(false);
        setVisible(false);
        setShowModal(false);
    }

    function handleSavePrefs() {
        saveConsent(prefs);
        logConsent("SAVE_PREFERENCES", prefs);
        if (prefs.analytics) {
            setGtmReady(true);
            loadGtmNow();
        } else {
            clearNonEssentialCookies();
            setGtmReady(false);
        }
        setVisible(false);
        setShowModal(false);
    }

    function toggleCategory(id) {
        setPrefs((p) => ({ ...p, [id]: !p[id] }));
    }

    return (
        <>
            {loadGtm && (
                <Script
                    id="gtm-consent"
                    strategy="afterInteractive"
                    src={`https://www.googletagmanager.com/gtm.js?id=${siteConfig.gtmId}`}
                />
            )}

            {showModal && (
                <div
                    className="fixed inset-0 bg-black/50 z-[9998] flex items-center justify-center p-4"
                    onClick={() => setShowModal(false)}
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-[760px] max-h-[90vh] overflow-y-auto shadow-2xl relative scrollbar-thin"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200 bg-white">
                            <div>
                                <h2 className="m-0 text-xl font-bold text-slate-900">
                                    Cookie Preferences
                                </h2>
                                <p className="mt-1 text-[13px] text-slate-500">
                                    Manage your cookie settings for velanapps.com
                                </p>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                aria-label="Close"
                                className="p-3 text-slate-400 hover:text-slate-600 bg-transparent border-none cursor-pointer text-[22px] leading-none transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="px-6 py-5">
                            <p className="text-sm text-slate-600 leading-relaxed mb-5">
                                When you visit, the website may store or retrieve information on your browser, mostly in the form of cookies.
                                This information might be about you, your preferences or your device, but does not usually directly identify you.
                                The information is mostly used to make the website display the embedded content you expect to see and work
                                the way you expect it to <Link href="/privacy-policy" className="text-primary font-semibold underline">privacy policy</Link>.
                            </p>

                            {COOKIE_CATEGORIES.map((cat) => (
                                <CategoryAccordion
                                    key={cat.id}
                                    category={cat}
                                    enabled={prefs[cat.id]}
                                    onToggle={toggleCategory}
                                />
                            ))}
                        </div>

                        <div className="sticky bottom-0 flex flex-wrap gap-2.5 justify-end px-6 py-4 border-t border-slate-200 bg-white">
                            <button
                                onClick={handleRejectAll}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border border-slate-300 bg-white text-gray-700 font-semibold text-sm cursor-pointer hover:bg-slate-50 transition-colors"
                            >
                                <X size={14} />
                                Reject All
                            </button>
                            <button
                                onClick={handleSavePrefs}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border border-red-600 bg-white text-red-600 font-semibold text-sm cursor-pointer hover:bg-red-50 transition-colors"
                            >
                                <Save size={14} />
                                Save Preferences
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border-none bg-red-gradient text-white font-semibold text-sm cursor-pointer hover:opacity-90 transition-opacity"
                            >
                                <ShieldCheck size={14} />
                                Accept All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {visible && !showModal && (
                <div className="cookie-banner fixed bottom-0 left-0 right-0 z-[9997] bg-slate-900/95 md:backdrop-blur-lg border-t border-white/10 text-white shadow-[0_-4px_24px_rgba(0,0,0,0.3)]">
                    <div className="max-w-[1200px] mx-auto px-4 py-3 md:px-6 md:py-5 flex flex-wrap items-center gap-3 md:gap-6">
                        <div className="flex-1 min-w-[260px]">
                            <div className="hidden md:flex items-center gap-2.5 mb-2">
                                <Cookie size={18} className="text-red-400" />
                                <span className="font-bold text-base">Choose Your Cookies Policy</span>
                            </div>
                            <p className="m-0 text-xs md:text-[13px] leading-snug md:leading-normal text-slate-200">
                                <Cookie size={14} className="md:hidden inline-block align-[-2px] mr-1.5 text-red-400" />
                                <span className="md:hidden">We use cookies to improve your experience.</span>
                                <span className="hidden md:inline">We use cookies on our website to provide you with a more personalized digital experience. To learn more about how we use cookies and how you can change your cookie settings, please refer to the following</span>{" "}
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="bg-transparent border-none text-red-400 cursor-pointer text-xs md:text-[13px] p-0 underline hover:text-red-300 transition-colors"
                                >
                                    cookie details
                                </button>
                            </p>
                        </div>

                        <div className="flex w-full md:w-auto md:flex-wrap items-center gap-2.5">
                            <button
                                onClick={() => setShowModal(true)}
                                aria-label="Manage Preferences"
                                className="order-last md:order-none ml-auto md:ml-0 px-1 md:px-[14px] py-2 rounded-lg md:border md:border-slate-400 bg-transparent text-slate-300 md:text-slate-100 underline md:no-underline font-semibold text-xs cursor-pointer whitespace-nowrap hover:text-white md:hover:border-slate-600 md:hover:bg-slate-600 transition-colors"
                            >
                                <span className="md:hidden">Manage</span>
                                <span className="hidden md:inline">Manage Preferences</span>
                            </button>
                            <button
                                onClick={handleRejectAll}
                                className="flex-1 md:flex-none px-[14px] py-2.5 md:py-2 rounded-lg border border-slate-400 bg-transparent text-slate-100 font-semibold text-xs cursor-pointer whitespace-nowrap hover:border-slate-600 hover:bg-slate-600 hover:text-white transition-colors"
                            >
                                Reject All
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="flex-1 md:flex-none px-[14px] py-2.5 md:py-2 rounded-lg border-none bg-rose-600 text-white font-bold text-xs cursor-pointer whitespace-nowrap hover:bg-rose-700 transition-colors"
                            >
                                Accept All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
